// src/components/HedgePositionPanel.jsx
//
// Renders side-by-side LONG and SHORT position panels for hedge-mode bots
// (futures hedge OR stock_equity with Phase 7 shorting). Used inside the
// bot card's Live Statistics section.
//
// When both long and short positions are zero, renders nothing (the
// regular position display takes over). When only one side is held,
// shows just that one panel highlighted. When both are held, shows
// both side by side so the user can see their hedge state clearly.
//
// Reads from the normalized stats object that includes both long
// (PositionQty, EntryPrice, PositionCost) and short
// (ShortPositionQty, ShortEntryPrice, ShortPositionCost) fields.

import React from 'react';
import { Box, Paper, Typography, Stack, Grid, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, ShowChart } from '@mui/icons-material';


export function HedgePositionPanel({ stats, quote, markPrice }) {
  if (!stats) return null;

  const longQty   = num(stats.PositionQty);
  const shortQty  = num(stats.ShortPositionQty);
  const isHedge   = longQty > 0 && shortQty > 0;
  const hasShort  = shortQty > 0;
  const hasLong   = longQty > 0;

  // If neither side held, render nothing — the regular Position group handles it
  if (!hasLong && !hasShort) return null;

  // If only long held and no short side ever opened, let the regular display handle it.
  // We only kick in for actual short-side activity or hedges.
  if (!hasShort) return null;

  const mkPrice = num(markPrice ?? stats.MarkPrice);

  // ── Long side ──
  const longEntry = num(stats.EntryPrice);
  const longCost  = num(stats.PositionCost);
  const longMark  = mkPrice > 0 ? longQty * mkPrice : null;
  const longPnl   = (longCost > 0 && longMark != null) ? longMark - longCost : null;
  const longPnlPct = (longCost > 0 && longPnl != null) ? (longPnl / longCost) * 100 : null;

  // ── Short side ──
  // For shorts, P&L is REVERSED: entry - mark (you profit when price drops).
  // PositionCost for shorts = qty * entry_price (the proceeds you received on opening).
  const shortEntry = num(stats.ShortEntryPrice);
  const shortCost  = num(stats.ShortPositionCost);
  const shortMark  = mkPrice > 0 ? shortQty * mkPrice : null;
  const shortPnl   = (shortCost > 0 && shortMark != null) ? shortCost - shortMark : null;
  const shortPnlPct = (shortCost > 0 && shortPnl != null) ? (shortPnl / shortCost) * 100 : null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2.5,
        p: 2,
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 2,
        bgcolor: '#070a0f',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ShowChart sx={{ fontSize: 16, color: '#4d8fc7' }} />
        <Typography variant="caption" sx={{ color: '#4d8fc7', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {isHedge ? 'Hedge Position' : 'Short Position'}
        </Typography>
        {isHedge && (
          <Chip
            size="small"
            label="LONG + SHORT"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: '#2a1a4a',
              color: '#4d8fc7',
              border: '1px solid #3f6fb0',
              ml: 'auto',
            }}
          />
        )}
      </Box>

      <Grid container spacing={2}>
        {/* LONG panel — only show if we have a long position */}
        {hasLong && (
          <Grid item xs={12} md={isHedge ? 6 : 12}>
            <SidePanel
              side="LONG"
              qty={longQty}
              entry={longEntry}
              cost={longCost}
              markValue={longMark}
              pnl={longPnl}
              pnlPct={longPnlPct}
              quote={quote}
              icon={<TrendingUp sx={{ fontSize: 14, color: '#46b88a' }} />}
              accentColor="#46b88a"
              accentBg="rgba(70,184,138, 0.05)"
              accentBorder="rgba(70,184,138, 0.2)"
            />
          </Grid>
        )}

        {/* SHORT panel — always shown in this component */}
        <Grid item xs={12} md={isHedge ? 6 : 12}>
          <SidePanel
            side="SHORT"
            qty={shortQty}
            entry={shortEntry}
            cost={shortCost}
            markValue={shortMark}
            pnl={shortPnl}
            pnlPct={shortPnlPct}
            quote={quote}
            icon={<TrendingDown sx={{ fontSize: 14, color: '#d9a441' }} />}
            accentColor="#d9a441"
            accentBg="rgba(217,164,65, 0.05)"
            accentBorder="rgba(217,164,65, 0.2)"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}


function SidePanel({ side, qty, entry, cost, markValue, pnl, pnlPct, quote, icon, accentColor, accentBg, accentBorder }) {
  const fmt = (v, digits = 2) => Number.isFinite(v)
    ? Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : '—';

  const fmtAsset = (v) => {
    if (!Number.isFinite(v)) return '—';
    const digits = Math.abs(v) < 1 ? 4 : 2;
    return `${fmt(v, digits)} ${quote || ''}`;
  };

  const pnlColor = pnl == null ? '#9aa6bd' : (pnl >= 0 ? '#46b88a' : '#d97b72');
  const pnlSign = pnl == null ? '' : (pnl >= 0 ? '+' : '−');

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{
            color: accentColor,
            fontWeight: 700,
            fontSize: '0.65rem',
            letterSpacing: 0.5,
          }}
        >
          {side}
        </Typography>
        <Typography variant="caption" sx={{ color: '#9aa6bd', ml: 'auto', fontFamily: 'monospace', fontSize: '0.65rem' }}>
          {fmt(qty, 4)} shares
        </Typography>
      </Box>

      <Stack spacing={0.5}>
        <Row label="Entry"     value={fmtAsset(entry)} />
        <Row label="Cost"      value={fmtAsset(cost)} />
        <Row label="Mark Value" value={fmtAsset(markValue)} />
        <Row
          label="Unrealized P&L"
          value={pnl == null
            ? '—'
            : `${pnlSign}${fmtAsset(Math.abs(pnl))}${pnlPct != null ? ` (${pnlSign}${Math.abs(pnlPct).toFixed(2)}%)` : ''}`
          }
          valueColor={pnlColor}
          bold
        />
      </Stack>
    </Box>
  );
}


function Row({ label, value, valueColor = '#f2f6ff', bold = false }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="caption" sx={{ color: '#9aa6bd', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: valueColor,
          fontFamily: 'monospace',
          fontWeight: bold ? 700 : 500,
          fontSize: '0.75rem',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}


function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}